import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import type { CreateAppointmentRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useAppointmentCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createAppointmentRequest: CreateAppointmentRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );

      const cleanedRequest: CreateAppointmentRequest = {
        ...createAppointmentRequest,
        description:
          createAppointmentRequest.description === ""
            ? null
            : (createAppointmentRequest.description ?? null),
        onlineMeetingUrl:
          createAppointmentRequest.onlineMeetingUrl === ""
            ? null
            : (createAppointmentRequest.onlineMeetingUrl ?? null),
        bookingNotice:
          createAppointmentRequest.bookingNotice === ""
            ? null
            : (createAppointmentRequest.bookingNotice ?? null),
        cancellationNotice:
          createAppointmentRequest.cancellationNotice === ""
            ? null
            : (createAppointmentRequest.cancellationNotice ?? null),
        appointmentTypeId: createAppointmentRequest.appointmentTypeId ?? null,
        startDate: createAppointmentRequest.startDate ?? null,
        endDate: createAppointmentRequest.endDate ?? null,
      };

      return service.apiAppointmentsPost({
        createAppointmentRequest: cleanedRequest,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      toast({
        title: "Private lesson created",
        description: "Private lesson created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create appointment";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
